import React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

export type ConsoleColumn<T> = {
  /** 列キー */
  key: string;
  /** ヘッダー表示名 */
  header: string;
  /** セルのレンダラ（ellipsis を指定する場合は不要） */
  render?: (row: T) => React.ReactNode;
  /** 列幅（sx width 相当） */
  width?: number | string;
  /** 右寄せ */
  align?: 'left' | 'right' | 'center';
  /**
   * 見切れる場合に折り返さず三点リーダー表示し、ホバーでツールチップを出す。
   * render の代わりに、この関数が返す文字列を1行省略表示する。
   */
  ellipsis?: (row: T) => string;
};

/**
 * 折り返さず三点リーダーで省略し、ホバー時に全文をツールチップ表示するセル。
 * コンソールテーブルの列で ellipsis を指定した場合に使用する。
 */
export const EllipsisText: React.FC<{ text: string; bold?: boolean }> = ({ text, bold }) => {
  if (!text) {
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        —
      </Box>
    );
  }
  return (
    <Tooltip title={text} enterDelay={400} placement="top-start">
      <Box
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: bold ? 600 : undefined,
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

type Props<T> = {
  columns: ConsoleColumn<T>[];
  rows: T[];
  /** 各行の一意キー */
  rowKey: (row: T) => string;
  /** 選択中の行キー */
  selectedKey?: string;
  onRowClick: (row: T) => void;
  /** 検索キーワード（制御） */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** 検索確定（Enter） */
  onSearchSubmit?: () => void;
  searchPlaceholder?: string;
  /** 検索バー右側の追加要素（フィルタトグル等） */
  filterActions?: React.ReactNode;
  /** 内蔵の検索バーを非表示にする（外部の検索UIを使う場合・任意） */
  hideSearch?: boolean;
  /** 検索バー領域に差し込むカスタム要素（内蔵検索の代わり・任意） */
  toolbar?: React.ReactNode;
  emptyMessage?: string;
  /** テーブル本体の高さ */
  height?: string;
};

/**
 * AWSコンソール風のリソーステーブル。
 * - 上部に検索バー（+ フィルタトグル）
 * - カラムヘッダー付きの table 行
 * - 行クリックで選択（選択行はアクセントの左線＋ハイライト）
 * MUIトーン（divider ボーダー・角丸・elevation 0）で表現する。
 */
function ConsoleTable<T>({
  columns,
  rows,
  rowKey,
  selectedKey,
  onRowClick,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = '検索',
  filterActions,
  hideSearch = false,
  toolbar,
  emptyMessage = 'リソースがありません',
  height = 'calc(100vh - 240px)',
}: Props<T>) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height }}>
      {/* 検索・フィルタバー（テーブル枠の外に独立配置） */}
      {!hideSearch && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, flexShrink: 0 }}>
          {toolbar ?? (
            <TextField
              size="small"
              fullWidth
              placeholder={searchPlaceholder}
              value={searchValue ?? ''}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearchSubmit?.();
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
          {filterActions}
        </Stack>
      )}

      {/* テーブル本体（枠付き） */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={{
                    width: col.width,
                    fontWeight: 700,
                    fontSize: 12,
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                const selected = key === selectedKey;
                return (
                  <TableRow
                    key={key}
                    hover
                    selected={selected}
                    onClick={() => onRowClick(row)}
                    sx={{
                      cursor: 'pointer',
                      '& > td': {
                        borderBottom: 1,
                        borderColor: 'divider',
                        // 選択行の左端アクセント
                        borderLeft: 3,
                        borderLeftColor: 'transparent',
                      },
                      '& > td:first-of-type': {
                        borderLeftColor: selected ? 'primary.main' : 'transparent',
                      },
                      '&.Mui-selected': { bgcolor: 'action.selected' },
                      '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        sx={{
                          fontSize: 13,
                          ...(col.ellipsis && {
                            maxWidth: col.width ?? 200,
                            overflow: 'hidden',
                          }),
                        }}
                      >
                        {col.ellipsis ? (
                          <EllipsisText text={col.ellipsis(row)} />
                        ) : (
                          col.render?.(row)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ConsoleTable;
